import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../components/AppIcon';
import CorporateSelect from '../../components/ui/CorporateSelect';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { tiendaDb } from '../../lib/supabase';
import { movimientosStockService, almacenesService } from '../../services/tiendaService';
import { supabase } from '../../lib/supabase';

const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';
const ORANGE = '#FB923C';
const RED = '#F87171';
const YELLOW = '#FBBF24';

const TIENDA_FISICA_ID = '4e60f99f-25da-4792-900e-fcf0c40501ab';

const fmt = (n, d = 2) => typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' ? `${fmt(n)} €` : '—';
const fmtPct = (n) => typeof n === 'number' ? `${fmt(n * 100, 1)}%` : '—';

const TABS = ['Existencias', 'Valoración', 'Rankings', 'Rotación', 'Alertas'];
const COLORS_CHART = [ACCENT, GOLD, BLUE, PURPLE, ORANGE, RED, YELLOW, '#38BDF8', '#4ADE80', '#F472B6'];

const getStockStatus = (item) => {
  const actual = item?.stock_actual ?? 0;
  const minimo = item?.producto?.stock_minimo ?? 0;
  if (actual <= 0) return 'Agotado';
  if (actual <= minimo) return 'Bajo mínimo';
  return 'Activo';
};

const StockBadge = ({ item }) => {
  const s = getStockStatus(item);
  const styles = {
    'Agotado': { background: 'rgba(248,113,113,0.15)', color: RED, border: '1px solid rgba(248,113,113,0.25)' },
    'Bajo mínimo': { background: 'rgba(251,191,36,0.15)', color: YELLOW, border: '1px solid rgba(251,191,36,0.25)' },
    'Activo': { background: 'rgba(52,211,153,0.12)', color: ACCENT, border: '1px solid rgba(52,211,153,0.25)' },
  };
  return <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={styles?.[s]}>{s}</span>;
};

// ─── MODAL MOVIMIENTO ─────────────────────────────────────────────────────────
const MovimientoModal = ({ item, almacenes, onSave, onClose }) => {
  const [form, setForm] = useState({ tipo_movimiento: 'ENTRADA', cantidad: '', coste_unitario: '', almacen_origen_id: item?.almacen_id || '', observaciones: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const tipoOpts = [{ value: 'ENTRADA', label: 'Entrada' }, { value: 'VENTA', label: 'Venta/Salida' }, { value: 'MERMA', label: 'Merma' }, { value: 'AJUSTE', label: 'Ajuste' }, { value: 'DEVOLUCION', label: 'Devolución' }];
  const almacenOpts = [{ value: '', label: 'Sin almacén' }, ...(almacenes || [])?.map(a => ({ value: a?.id, label: a?.nombre }))];

  const handleSave = async () => {
    if (!form?.cantidad || parseFloat(form?.cantidad) <= 0) { setErr('La cantidad debe ser mayor que 0'); return; }
    try {
      setSaving(true);
      await onSave({ producto_id: item?.producto_id, tipo_movimiento: form?.tipo_movimiento, cantidad: parseFloat(form?.cantidad), coste_unitario: form?.coste_unitario !== '' ? parseFloat(form?.coste_unitario) : null, almacen_origen_id: form?.almacen_origen_id || null, observaciones: form?.observaciones || null });
      onClose();
    } catch (e) { setErr(e?.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.80)' }}>
      <div className="w-full max-w-md rounded-2xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-bold text-white">Registrar Movimiento</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="font-oranienbaum text-white/60 text-sm">{item?.producto?.nombre}</p>
          {err && <div className="p-3 rounded-xl text-xs font-oranienbaum text-red-400" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>{err}</div>}
          <div>
            <label className="block font-oranienbaum text-white/50 text-xs mb-1">Tipo</label>
            <CorporateSelect value={form?.tipo_movimiento} onChange={v => set('tipo_movimiento', v)} options={tipoOpts} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Cantidad *</label>
              <input type="number" value={form?.cantidad} onChange={e => set('cantidad', e?.target?.value)} className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} min="0" step="0.001" />
            </div>
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Coste unitario (€)</label>
              <input type="number" value={form?.coste_unitario} onChange={e => set('coste_unitario', e?.target?.value)} className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} min="0" step="0.01" />
            </div>
          </div>
          <div>
            <label className="block font-oranienbaum text-white/50 text-xs mb-1">Almacén</label>
            <CorporateSelect value={form?.almacen_origen_id} onChange={v => set('almacen_origen_id', v)} options={almacenOpts} />
          </div>
          <div>
            <label className="block font-oranienbaum text-white/50 text-xs mb-1">Observaciones</label>
            <input type="text" value={form?.observaciones} onChange={e => set('observaciones', e?.target?.value)} className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
          </div>
        </div>
        <div className="px-5 py-4 flex justify-end gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-oranienbaum text-white/40" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-sm font-oranienbaum" style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
            {saving ? 'Guardando...' : 'Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PESTAÑA EXISTENCIAS ──────────────────────────────────────────────────────
const TabExistencias = ({ inventario, familias, almacenes, loading, onMovimiento, onRefresh }) => {
  const [search, setSearch] = useState('');
  const [filterFamilia, setFilterFamilia] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAlmacen, setFilterAlmacen] = useState(TIENDA_FISICA_ID);

  const famOpts = [{ value: '', label: 'Todas las familias' }, ...familias?.map(f => ({ value: f?.id, label: f?.nombre }))];
  const statusOpts = [{ value: '', label: 'Todos' }, { value: 'Activo', label: 'Activo' }, { value: 'Bajo mínimo', label: 'Bajo mínimo' }, { value: 'Agotado', label: 'Agotado' }];
  const almacenOpts = [{ value: '', label: 'Todos los almacenes' }, ...(almacenes || [])?.map(a => ({ value: a?.id, label: a?.nombre }))];

  const filtered = inventario?.filter(item => {
    if (search) {
      const s = search?.toLowerCase();
      const matchNombre = item?.producto?.nombre?.toLowerCase()?.includes(s);
      const matchCodigo = item?.producto?.codigo_interno?.toLowerCase()?.includes(s);
      const matchPlu = item?.producto?.plu?.toLowerCase()?.includes(s);
      const matchEan = item?.producto?.ean?.toLowerCase()?.includes(s);
      if (!matchNombre && !matchCodigo && !matchPlu && !matchEan) return false;
    }
    if (filterFamilia && item?.producto?.familia_id !== filterFamilia) return false;
    if (filterStatus && getStockStatus(item) !== filterStatus) return false;
    if (filterAlmacen && item?.almacen_id !== filterAlmacen) return false;
    return true;
  });

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-48 px-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', height: '38px' }}>
          <Icon name="Search" size={14} className="text-white/30 flex-shrink-0" />
          <input type="text" value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Buscar producto, PLU, EAN..." className="flex-1 bg-transparent outline-none font-oranienbaum text-white/80 text-sm" />
        </div>
        <div className="w-44"><CorporateSelect value={filterFamilia} onChange={setFilterFamilia} options={famOpts} searchable /></div>
        <div className="w-36"><CorporateSelect value={filterStatus} onChange={setFilterStatus} options={statusOpts} /></div>
        <div className="w-44"><CorporateSelect value={filterAlmacen} onChange={setFilterAlmacen} options={almacenOpts} /></div>
      </div>
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                {['Producto', 'PLU', 'EAN', 'Familia', 'Almacén', 'Stock', 'Reservado', 'Disponible', 'Mín.', 'Coste s/IVA', 'Coste c/IVA', 'PVP', 'Valor Coste', 'Valor PVP', 'Margen Pot.', 'Estado', 'Acciones']?.map(h => (
                  <th key={h} className="px-3 py-3 text-left font-oranienbaum text-white/30 text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={17} className="px-4 py-12 text-center"><div className="flex items-center justify-center gap-3"><div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-white/60 animate-spin" /><span className="font-oranienbaum text-white/40 text-sm">Cargando...</span></div></td></tr>
              ) : filtered?.length === 0 ? (
                <tr><td colSpan={17} className="px-4 py-12 text-center"><p className="font-oranienbaum text-white/30 text-sm">Sin existencias</p></td></tr>
              ) : filtered?.map(item => {
                const coste = item?.ultimo_coste || item?.producto?.precio_compra || 0;
                const iva = item?.producto?.tipo_iva || 10;
                const costeConIva = coste * (1 + iva / 100);
                const pvp = item?.producto?.pvp_venta || 0;
                const stock = item?.stock_actual || 0;
                const valorCoste = stock * coste;
                const valorPvp = stock * pvp;
                const pvpSinIva = item?.producto?.pvp_sin_iva || 0;
                const margenPot = stock * (pvpSinIva - coste);
                return (
                  <tr key={item?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-3 py-2.5 max-w-[160px]"><p className="font-oranienbaum text-white/85 text-sm truncate">{item?.producto?.nombre || '—'}</p><p className="font-oranienbaum text-white/30 text-xs">{item?.producto?.codigo_interno || ''}</p></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: BLUE }}>{item?.producto?.plu || '—'}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs text-white/50">{item?.producto?.ean || '—'}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: GOLD }}>{item?.producto?.familia?.nombre || '—'}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-white/50 text-xs">{item?.almacen?.nombre || 'Principal'}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-white/85 text-sm font-bold">{fmt(item?.stock_actual, 2)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-white/40 text-xs">{fmt(item?.stock_reservado, 2)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: ACCENT }}>{fmt(item?.stock_disponible, 2)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-white/40 text-xs">{fmt(item?.producto?.stock_minimo, 0)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-white/70 text-xs">{fmtEur(coste)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: BLUE }}>{fmtEur(costeConIva)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: GOLD }}>{fmtEur(pvp)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: BLUE }}>{fmtEur(valorCoste)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: ACCENT }}>{fmtEur(valorPvp)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: margenPot >= 0 ? GOLD : RED }}>{fmtEur(margenPot)}</span></td>
                    <td className="px-3 py-2.5"><StockBadge item={item} /></td>
                    <td className="px-3 py-2.5">
                      <button onClick={() => onMovimiento(item)} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors"><Icon name="ArrowUpDown" size={13} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── PESTAÑA VALORACIÓN ───────────────────────────────────────────────────────
const TabValoracion = ({ inventario, familias }) => {
  const totalCoste = inventario?.reduce((s, i) => s + (i?.stock_actual || 0) * (i?.ultimo_coste || i?.producto?.precio_compra || 0), 0);
  const totalCosteIva = inventario?.reduce((s, i) => {
    const c = i?.ultimo_coste || i?.producto?.precio_compra || 0;
    const iva = i?.producto?.tipo_iva || 10;
    return s + (i?.stock_actual || 0) * c * (1 + iva / 100);
  }, 0);
  const totalPvp = inventario?.reduce((s, i) => s + (i?.stock_actual || 0) * (i?.producto?.pvp_venta || 0), 0);
  const totalMargen = inventario?.reduce((s, i) => {
    const c = i?.ultimo_coste || i?.producto?.precio_compra || 0;
    const pvpSinIva = i?.producto?.pvp_sin_iva || 0;
    return s + (i?.stock_actual || 0) * (pvpSinIva - c);
  }, 0);
  const totalUnidades = inventario?.reduce((s, i) => s + (i?.stock_actual || 0), 0);
  const productosConStock = inventario?.filter(i => (i?.stock_actual || 0) > 0)?.length;

  const byFamilia = {};
  inventario?.forEach(item => {
    const fam = item?.producto?.familia?.nombre || 'Sin familia';
    if (!byFamilia?.[fam]) byFamilia[fam] = { nombre: fam, valorCoste: 0, valorPvp: 0, margen: 0 };
    const c = item?.ultimo_coste || item?.producto?.precio_compra || 0;
    const pvp = item?.producto?.pvp_venta || 0;
    const pvpSinIva = item?.producto?.pvp_sin_iva || 0;
    const stock = item?.stock_actual || 0;
    byFamilia[fam].valorCoste += stock * c;
    byFamilia[fam].valorPvp += stock * pvp;
    byFamilia[fam].margen += stock * (pvpSinIva - c);
  });
  const famData = Object.values(byFamilia)?.sort((a, b) => b?.valorCoste - a?.valorCoste)?.slice(0, 10);

  const kpis = [
    { label: 'Valor total a coste s/IVA', value: fmtEur(totalCoste), icon: 'Package', color: BLUE },
    { label: 'Valor total a coste c/IVA', value: fmtEur(totalCosteIva), icon: 'Package', color: PURPLE },
    { label: 'Valor potencial de venta', value: fmtEur(totalPvp), icon: 'TrendingUp', color: ACCENT },
    { label: 'Margen potencial', value: fmtEur(totalMargen), icon: 'DollarSign', color: GOLD },
    { label: 'Unidades en stock', value: fmt(totalUnidades, 0), icon: 'BarChart2', color: ORANGE },
    { label: 'Productos con stock', value: String(productosConStock), icon: 'CheckCircle', color: ACCENT },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis?.map((k, i) => (
          <div key={i} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ background: `${k?.color}15`, border: `1px solid ${k?.color}30` }}>
              <Icon name={k?.icon} size={15} style={{ color: k?.color }} />
            </div>
            <p className="font-oranienbaum font-bold text-white text-lg">{k?.value}</p>
            <p className="font-oranienbaum text-white/40 text-xs leading-tight">{k?.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-semibold text-white text-sm mb-4">Valor de stock por familia (coste)</h3>
          {famData?.length === 0 ? <p className="font-oranienbaum text-white/25 text-xs text-center py-8">Sin datos</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={famData} layout="vertical" barSize={12}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.30)', fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000)?.toFixed(0)}k`} />
                <YAxis type="category" dataKey="nombre" tick={{ fill: 'rgba(255,255,255,0.50)', fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} width={90} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={v => fmtEur(v)} />
                <Bar dataKey="valorCoste" fill={BLUE} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-semibold text-white text-sm mb-4">Margen potencial por familia</h3>
          {famData?.length === 0 ? <p className="font-oranienbaum text-white/25 text-xs text-center py-8">Sin datos</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={famData} layout="vertical" barSize={12}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.30)', fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000)?.toFixed(0)}k`} />
                <YAxis type="category" dataKey="nombre" tick={{ fill: 'rgba(255,255,255,0.50)', fontSize: 10, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} width={90} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={v => fmtEur(v)} />
                <Bar dataKey="margen" fill={GOLD} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── PESTAÑA RANKINGS ─────────────────────────────────────────────────────────
const TabRankings = ({ inventario, familias }) => {
  const [topN, setTopN] = useState('10');
  const [filterFamilia, setFilterFamilia] = useState('');

  const famOpts = [{ value: '', label: 'Todas las familias' }, ...familias?.map(f => ({ value: f?.id, label: f?.nombre }))];
  const topOpts = [{ value: '5', label: 'Top 5' }, { value: '10', label: 'Top 10' }, { value: '999', label: 'Todos' }];

  const filtered = filterFamilia ? inventario?.filter(i => i?.producto?.familia_id === filterFamilia) : inventario;
  const n = parseInt(topN) || 10;

  const enrich = (item) => {
    const coste = item?.ultimo_coste || item?.producto?.precio_compra || 0;
    const pvp = item?.producto?.pvp_venta || 0;
    const pvpSinIva = item?.producto?.pvp_sin_iva || 0;
    const stock = item?.stock_actual || 0;
    return { ...item, _coste: coste, _pvp: pvp, _pvpSinIva: pvpSinIva, _stock: stock, _valorCoste: stock * coste, _valorPvp: stock * pvp, _margenPot: stock * (pvpSinIva - coste), _margenPct: pvpSinIva > 0 ? (pvpSinIva - coste) / pvpSinIva : 0 };
  };
  const enriched = filtered?.map(enrich);

  const rankings = [
    { title: 'Mayor valor de stock (coste)', data: [...enriched]?.sort((a, b) => b?._valorCoste - a?._valorCoste)?.slice(0, n), key: '_valorCoste', fmt: fmtEur, color: BLUE },
    { title: 'Mayor valor potencial (PVP)', data: [...enriched]?.sort((a, b) => b?._valorPvp - a?._valorPvp)?.slice(0, n), key: '_valorPvp', fmt: fmtEur, color: ACCENT },
    { title: 'Mayor margen porcentual', data: [...enriched]?.filter(i => i?._pvpSinIva > 0)?.sort((a, b) => b?._margenPct - a?._margenPct)?.slice(0, n), key: '_margenPct', fmt: v => fmtPct(v), color: GOLD },
    { title: 'Mayor margen potencial (€)', data: [...enriched]?.sort((a, b) => b?._margenPot - a?._margenPot)?.slice(0, n), key: '_margenPot', fmt: fmtEur, color: GOLD },
    { title: 'Mayor stock', data: [...enriched]?.sort((a, b) => b?._stock - a?._stock)?.slice(0, n), key: '_stock', fmt: v => fmt(v, 0), color: PURPLE },
    { title: 'Menor stock (con stock)', data: [...enriched]?.filter(i => i?._stock > 0)?.sort((a, b) => a?._stock - b?._stock)?.slice(0, n), key: '_stock', fmt: v => fmt(v, 0), color: ORANGE },
    { title: 'Bajo mínimo', data: enriched?.filter(i => i?._stock > 0 && i?._stock <= (i?.producto?.stock_minimo || 0)), key: '_stock', fmt: v => fmt(v, 0), color: RED },
    { title: 'Sin PVP', data: enriched?.filter(i => !i?._pvp), key: '_stock', fmt: v => fmt(v, 0), color: RED },
    { title: 'Sin familia', data: enriched?.filter(i => !i?.producto?.familia_id), key: '_stock', fmt: v => fmt(v, 0), color: ORANGE },
  ];

  const RankingTable = ({ title, data, key: k, fmt: fmtFn, color }) => {
    const maxVal = data?.length > 0 ? Math.max(...data?.map(d => Math.abs(d?.[k] || 0))) : 1;
    return (
      <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <h4 className="font-oranienbaum font-semibold text-white/80 text-xs mb-3 uppercase tracking-wider">{title}</h4>
        {data?.length === 0 ? (
          <p className="font-oranienbaum text-white/25 text-xs text-center py-4">Sin datos</p>
        ) : (
          <div className="space-y-2">
            {data?.map((item, idx) => {
              const val = item?.[k] || 0;
              const pct = maxVal > 0 ? Math.abs(val) / maxVal * 100 : 0;
              return (
                <div key={item?.id || idx}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-oranienbaum text-white/70 text-xs truncate max-w-[60%]">{item?.producto?.nombre || '—'}</span>
                    <span className="font-oranienbaum text-xs font-bold" style={{ color }}>{fmtFn(val)}</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color, opacity: 0.7 }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="w-36"><CorporateSelect value={topN} onChange={setTopN} options={topOpts} /></div>
        <div className="w-44"><CorporateSelect value={filterFamilia} onChange={setFilterFamilia} options={famOpts} searchable /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rankings?.map((r, i) => <RankingTable key={i} {...r} />)}
      </div>
    </div>
  );
};

// ─── PESTAÑA ROTACIÓN ─────────────────────────────────────────────────────────
const TabRotacion = ({ inventario }) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <Icon name="RotateCcw" size={28} className="text-white/20" />
      </div>
      <p className="font-oranienbaum text-white/40 text-base text-center">Aún no existen suficientes ventas para calcular la rotación.</p>
      <p className="font-oranienbaum text-white/25 text-sm text-center max-w-md">Esta pestaña mostrará los productos más y menos vendidos, días de inventario y rotación cuando existan datos de ventas reales.</p>
    </div>
  );
};

// ─── PESTAÑA ALERTAS ─────────────────────────────────────────────────────────
const TabAlertas = ({ inventario }) => {
  const sinStock = inventario?.filter(i => (i?.stock_actual || 0) <= 0 && i?.producto?.activo);
  const bajoMinimo = inventario?.filter(i => (i?.stock_actual || 0) > 0 && (i?.stock_actual || 0) <= (i?.producto?.stock_minimo || 0));
  const sinFamilia = inventario?.filter(i => !i?.producto?.familia_id);
  const sinPvp = inventario?.filter(i => !i?.producto?.pvp_venta && !i?.producto?.pvp_sin_iva);
  const margenBajo = inventario?.filter(i => i?.producto?.margen_real != null && i?.producto?.margen_objetivo != null && i?.producto?.margen_real < i?.producto?.margen_objetivo * 0.85);
  const pvpBloqueado = inventario?.filter(i => i?.producto?.bloqueo_precio);

  const AlertSection = ({ title, items, color, icon }) => (
    <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
          <Icon name={icon} size={14} style={{ color }} />
        </div>
        <h4 className="font-oranienbaum font-semibold text-white/80 text-sm">{title}</h4>
        <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-oranienbaum font-bold" style={{ background: `${color}15`, color }}>{items?.length}</span>
      </div>
      {items?.length === 0 ? (
        <p className="font-oranienbaum text-white/25 text-xs text-center py-3">Sin alertas</p>
      ) : (
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {items?.slice(0, 20)?.map(item => (
            <div key={item?.id} className="flex items-center justify-between py-1 px-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <span className="font-oranienbaum text-white/70 text-xs truncate">{item?.producto?.nombre || '—'}</span>
              <span className="font-oranienbaum text-xs ml-2 flex-shrink-0" style={{ color }}>{fmt(item?.stock_actual, 2)}</span>
            </div>
          ))}
          {items?.length > 20 && <p className="font-oranienbaum text-white/25 text-xs text-center">+{items?.length - 20} más</p>}
        </div>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <AlertSection title="Sin stock" items={sinStock} color={RED} icon="XCircle" />
      <AlertSection title="Bajo mínimo" items={bajoMinimo} color={YELLOW} icon="AlertTriangle" />
      <AlertSection title="Sin familia" items={sinFamilia} color={ORANGE} icon="Tag" />
      <AlertSection title="Sin PVP" items={sinPvp} color={RED} icon="DollarSign" />
      <AlertSection title="Margen bajo" items={margenBajo} color={ORANGE} icon="TrendingDown" />
      <AlertSection title="PVP bloqueado" items={pvpBloqueado} color={BLUE} icon="Lock" />
    </div>
  );
};

// ─── MODAL CONFIRMAR RECUENTO ─────────────────────────────────────────────────
const ModalConfirmarRecuento = ({ items, onConfirm, onClose, saving }) => {
  const conDiferencia = items?.filter(i => i?.diferencia !== 0 && i?.stockContado !== '');
  const positivos = conDiferencia?.filter(i => i?.diferencia > 0);
  const negativos = conDiferencia?.filter(i => i?.diferencia < 0);
  const revisados = items?.filter(i => i?.stockContado !== '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-lg rounded-2xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-bold text-white text-lg">Confirmar Recuento</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Productos revisados', value: revisados?.length, color: BLUE },
              { label: 'Con diferencia', value: conDiferencia?.length, color: YELLOW },
              { label: 'Ajustes positivos', value: positivos?.length, color: ACCENT },
              { label: 'Ajustes negativos', value: negativos?.length, color: RED },
            ]?.map((k, i) => (
              <div key={i} className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <span className="font-oranienbaum font-bold text-xl" style={{ color: k?.color }}>{k?.value}</span>
                <span className="font-oranienbaum text-white/50 text-xs">{k?.label}</span>
              </div>
            ))}
          </div>
          {conDiferencia?.length === 0 ? (
            <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.20)' }}>
              <p className="font-oranienbaum text-green-400 text-sm">No hay diferencias que ajustar.</p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {['Producto', 'Sistema', 'Contado', 'Diferencia']?.map(h => (
                        <th key={h} className="px-3 py-2 text-left font-oranienbaum text-white/30 text-xs">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {conDiferencia?.map(item => (
                      <tr key={item?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td className="px-3 py-2 font-oranienbaum text-white/70 text-xs truncate max-w-[120px]">{item?.nombre}</td>
                        <td className="px-3 py-2 font-oranienbaum text-white/50 text-xs">{item?.esPeso ? `${fmt(item?.stockSistema, 3)} kg` : `${fmt(item?.stockSistema, 0)} ud`}</td>
                        <td className="px-3 py-2 font-oranienbaum text-white/70 text-xs">{item?.esPeso ? `${fmt(parseFloat(item?.stockContado), 3)} kg` : `${fmt(parseFloat(item?.stockContado), 0)} ud`}</td>
                        <td className="px-3 py-2 font-oranienbaum text-xs font-bold" style={{ color: item?.diferencia > 0 ? ACCENT : RED }}>
                          {item?.diferencia > 0 ? '+' : ''}{item?.esPeso ? `${fmt(item?.diferencia, 3)} kg` : `${fmt(item?.diferencia, 0)} ud`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <p className="font-oranienbaum text-white/30 text-xs">Se crearán {conDiferencia?.length} movimiento(s) AJUSTE. El trigger actualizará inventario_actual automáticamente.</p>
        </div>
        <div className="px-5 py-4 flex justify-end gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-xl text-sm font-oranienbaum text-white/40" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={onConfirm} disabled={saving || conDiferencia?.length === 0} className="px-5 py-2 rounded-xl text-sm font-oranienbaum font-semibold" style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT, opacity: conDiferencia?.length === 0 ? 0.5 : 1 }}>
            {saving ? 'Aplicando...' : `Aplicar ${conDiferencia?.length} ajuste(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── VISTA RECUENTO FÍSICO ────────────────────────────────────────────────────
const VistaRecuentoFisico = ({ familias, onRefreshInventario }) => {
  const [productos, setProductos] = useState([]);
  const [recuento, setRecuento] = useState({}); // { [producto_id]: stockContado string }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterFamilia, setFilterFamilia] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [modoInicial, setModoInicial] = useState(false);
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const searchRef = useRef(null);

  // Cargar todos los productos + su stock en Tienda física
  const loadProductos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Cargar todos los productos activos
      const { data: prods, error: eProds } = await tiendaDb
        ?.from('productos')
        ?.select('id, nombre, codigo_interno, plu, ean, venta_por_peso, familia_id, familia:familias_producto(id, nombre)')
        ?.eq('activo', true)
        ?.order('nombre');
      if (eProds) throw eProds;

      // Cargar inventario_actual para Tienda física
      const { data: inv, error: eInv } = await tiendaDb
        ?.from('inventario_actual')
        ?.select('producto_id, stock_actual')
        ?.eq('almacen_id', TIENDA_FISICA_ID);
      if (eInv) throw eInv;

      // Construir mapa de stock
      const stockMap = {};
      inv?.forEach(i => { stockMap[i?.producto_id] = i?.stock_actual ?? 0; });

      // Combinar
      const combinados = (prods || [])?.map(p => ({
        id: p?.id,
        nombre: p?.nombre,
        codigo_interno: p?.codigo_interno,
        plu: p?.plu,
        ean: p?.ean,
        esPeso: p?.venta_por_peso,
        familia_id: p?.familia_id,
        familia_nombre: p?.familia?.nombre || '—',
        stockSistema: stockMap?.[p?.id] ?? 0,
      }));

      setProductos(combinados);
    } catch (e) { setError(e?.message || 'Error al cargar productos'); }
    setLoading(false);
  }, []);

  const loadHistorial = useCallback(async () => {
    setLoadingHistorial(true);
    try {
      const { data, error: eH } = await tiendaDb
        ?.from('movimientos_stock')
        ?.select(`
          id, created_at, cantidad, observaciones, usuario,
          producto:productos(nombre, plu)
        `)
        ?.eq('tipo_movimiento', 'AJUSTE')
        ?.eq('referencia_tipo', 'RECUENTO_INVENTARIO')
        ?.order('created_at', { ascending: false })
        ?.limit(100);
      if (!eH) setHistorial(data || []);
    } catch (_) {}
    setLoadingHistorial(false);
  }, []);

  useEffect(() => {
    loadProductos();
    loadHistorial();
  }, [loadProductos, loadHistorial]);

  // Búsqueda con EAN exacto → auto-focus
  const handleSearch = (val) => {
    setSearch(val);
  };

  const getStockContado = (id) => recuento?.[id] ?? '';
  const setStockContado = (id, val) => setRecuento(p => ({ ...p, [id]: val }));

  const getDiferencia = (prod) => {
    const contadoStr = getStockContado(prod?.id);
    if (contadoStr === '') return null;
    const contado = parseFloat(contadoStr);
    if (isNaN(contado)) return null;
    return contado - prod?.stockSistema;
  };

  // Filtrar productos
  const filtered = productos?.filter(prod => {
    if (search) {
      const s = search?.toLowerCase()?.trim();
      // EAN exacto → match directo
      if (prod?.ean && prod?.ean === s) return true;
      const matchNombre = prod?.nombre?.toLowerCase()?.includes(s);
      const matchPlu = prod?.plu?.toLowerCase()?.includes(s);
      const matchEan = prod?.ean?.toLowerCase()?.includes(s);
      const matchCodigo = prod?.codigo_interno?.toLowerCase()?.includes(s);
      if (!matchNombre && !matchPlu && !matchEan && !matchCodigo) return false;
    }
    if (filterFamilia && prod?.familia_id !== filterFamilia) return false;
    if (filterTipo === 'peso' && !prod?.esPeso) return false;
    if (filterTipo === 'unidad' && prod?.esPeso) return false;
    if (filterEstado === 'pendientes' && getStockContado(prod?.id) !== '') return false;
    if (filterEstado === 'contados' && getStockContado(prod?.id) === '') return false;
    if (filterEstado === 'diferencias') {
      const dif = getDiferencia(prod);
      if (dif === null || dif === 0) return false;
    }
    return true;
  });

  // KPIs recuento
  const totalProductos = productos?.length;
  const contados = productos?.filter(p => getStockContado(p?.id) !== '')?.length;
  const pendientes = totalProductos - contados;
  const conDiferencias = productos?.filter(p => {
    const d = getDiferencia(p);
    return d !== null && d !== 0;
  })?.length;
  const ajustesPos = productos?.filter(p => { const d = getDiferencia(p); return d !== null && d > 0; })?.length;
  const ajustesNeg = productos?.filter(p => { const d = getDiferencia(p); return d !== null && d < 0; })?.length;

  // Preparar items para modal
  const itemsParaModal = productos
    ?.filter(p => getStockContado(p?.id) !== '')
    ?.map(p => ({
      ...p,
      stockContado: getStockContado(p?.id),
      diferencia: getDiferencia(p),
    }));

  // Aplicar recuento
  const handleAplicar = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const email = user?.email || null;

      const conDif = itemsParaModal?.filter(i => i?.diferencia !== 0 && i?.diferencia !== null);
      if (conDif?.length === 0) { setSaving(false); setShowConfirmar(false); return; }

      const movimientos = conDif?.map(item => {
        const diferencia = item?.diferencia;
        return {
          producto_id: item?.id,
          tipo_movimiento: 'AJUSTE',
          cantidad: diferencia,
          almacen_origen_id: diferencia < 0 ? TIENDA_FISICA_ID : null,
          almacen_destino_id: diferencia > 0 ? TIENDA_FISICA_ID : null,
          referencia_tipo: 'RECUENTO_INVENTARIO',
          observaciones: `Recuento físico Tienda física${modoInicial ? ' (Inventario inicial)' : ''}`,
          usuario: email,
        };
      });

      const { error: eInsert } = await tiendaDb?.from('movimientos_stock')?.insert(movimientos);
      if (eInsert) throw eInsert;

      // Limpiar recuento
      setRecuento({});
      setShowConfirmar(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      await loadProductos();
      await loadHistorial();
      onRefreshInventario?.();
    } catch (e) {
      setSaveError(e?.message || 'Error al aplicar el recuento');
    }
    setSaving(false);
  };

  const famOpts = [{ value: '', label: 'Todas las familias' }, ...familias?.map(f => ({ value: f?.id, label: f?.nombre }))];
  const estadoOpts = [
    { value: 'todos', label: 'Todos' },
    { value: 'pendientes', label: 'Pendientes' },
    { value: 'contados', label: 'Contados' },
    { value: 'diferencias', label: 'Con diferencias' },
  ];
  const tipoOpts = [
    { value: 'todos', label: 'Todos' },
    { value: 'peso', label: 'Por peso (kg)' },
    { value: 'unidad', label: 'Por unidades (ud)' },
  ];

  return (
    <div className="space-y-5">
      {/* Toggle modo inicial */}
      <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <button
          onClick={() => setModoInicial(p => !p)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
          style={modoInicial ? { background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD } : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}
        >
          <Icon name={modoInicial ? 'ToggleRight' : 'ToggleLeft'} size={14} />
          Inventario inicial
        </button>
        {modoInicial && (
          <p className="font-oranienbaum text-xs" style={{ color: GOLD }}>
            ⚠️ Este recuento establecerá el punto de partida real del inventario de Tienda física.
          </p>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total productos', value: totalProductos, color: BLUE, icon: 'Package' },
          { label: 'Contados', value: contados, color: ACCENT, icon: 'CheckCircle' },
          { label: 'Pendientes', value: pendientes, color: YELLOW, icon: 'Clock' },
          { label: 'Con diferencias', value: conDiferencias, color: ORANGE, icon: 'AlertTriangle' },
          { label: 'Ajustes +', value: ajustesPos, color: ACCENT, icon: 'TrendingUp' },
          { label: 'Ajustes −', value: ajustesNeg, color: RED, icon: 'TrendingDown' },
        ]?.map((k, i) => (
          <div key={i} className="rounded-2xl p-3 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${k?.color}15`, border: `1px solid ${k?.color}30` }}>
              <Icon name={k?.icon} size={13} style={{ color: k?.color }} />
            </div>
            <div>
              <p className="font-oranienbaum font-bold text-white text-base">{k?.value}</p>
              <p className="font-oranienbaum text-white/40 text-[10px] leading-tight">{k?.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Buscador y filtros */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-64 px-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', height: '38px' }}>
          <Icon name="Search" size={14} className="text-white/30 flex-shrink-0" />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={e => handleSearch(e?.target?.value)}
            placeholder="Buscar o escanear producto, PLU, EAN..."
            className="flex-1 bg-transparent outline-none font-oranienbaum text-white/80 text-sm"
          />
          {search && <button onClick={() => setSearch('')} className="text-white/30 hover:text-white/60"><Icon name="X" size={12} /></button>}
        </div>
        <div className="w-44"><CorporateSelect value={filterFamilia} onChange={setFilterFamilia} options={famOpts} searchable /></div>
        <div className="w-40"><CorporateSelect value={filterEstado} onChange={setFilterEstado} options={estadoOpts} /></div>
        <div className="w-40"><CorporateSelect value={filterTipo} onChange={setFilterTipo} options={tipoOpts} /></div>
        <button
          onClick={() => setShowConfirmar(true)}
          disabled={contados === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold transition-all"
          style={{ background: contados > 0 ? `${ACCENT}20` : 'rgba(255,255,255,0.05)', border: `1px solid ${contados > 0 ? ACCENT + '40' : 'rgba(255,255,255,0.10)'}`, color: contados > 0 ? ACCENT : 'rgba(255,255,255,0.30)' }}
        >
          <Icon name="ClipboardCheck" size={14} />
          Aplicar Recuento
        </button>
      </div>

      {/* Mensajes */}
      {saveSuccess && (
        <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)' }}>
          <Icon name="CheckCircle" size={15} className="text-green-400 flex-shrink-0" />
          <p className="font-oranienbaum text-green-400 text-sm">Recuento aplicado correctamente. Los ajustes han sido registrados.</p>
        </div>
      )}
      {saveError && (
        <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}>
          <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{saveError}</p>
        </div>
      )}
      {error && (
        <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)' }}>
          <Icon name="AlertCircle" size={15} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Tabla recuento */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                {['Producto', 'PLU', 'EAN', 'Familia', 'Tipo', 'Stock sistema', 'Stock contado', 'Diferencia', 'Estado']?.map(h => (
                  <th key={h} className="px-3 py-3 text-left font-oranienbaum text-white/30 text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center"><div className="flex items-center justify-center gap-3"><div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-white/60 animate-spin" /><span className="font-oranienbaum text-white/40 text-sm">Cargando...</span></div></td></tr>
              ) : filtered?.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center"><p className="font-oranienbaum text-white/30 text-sm">Sin productos</p></td></tr>
              ) : filtered?.map(prod => {
                const contadoStr = getStockContado(prod?.id);
                const contado = contadoStr !== '' ? parseFloat(contadoStr) : null;
                const diferencia = contado !== null && !isNaN(contado) ? contado - prod?.stockSistema : null;
                const difColor = diferencia === null ? 'rgba(255,255,255,0.30)' : diferencia > 0 ? ACCENT : diferencia < 0 ? RED : 'rgba(255,255,255,0.50)';
                const estadoLabel = diferencia === null ? '—' : diferencia === 0 ? '✓ Correcto' : diferencia > 0 ? `+${prod?.esPeso ? fmt(diferencia, 3) : fmt(diferencia, 0)}` : `${prod?.esPeso ? fmt(diferencia, 3) : fmt(diferencia, 0)}`;

                return (
                  <tr key={prod?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-3 py-2.5 max-w-[180px]">
                      <p className="font-oranienbaum text-white/85 text-sm truncate">{prod?.nombre || '—'}</p>
                      <p className="font-oranienbaum text-white/30 text-xs">{prod?.codigo_interno || ''}</p>
                    </td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: BLUE }}>{prod?.plu || '—'}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs text-white/50">{prod?.ean || '—'}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: GOLD }}>{prod?.familia_nombre}</span></td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={prod?.esPeso ? { background: 'rgba(96,165,250,0.12)', color: BLUE, border: '1px solid rgba(96,165,250,0.25)' } : { background: 'rgba(167,139,250,0.12)', color: PURPLE, border: '1px solid rgba(167,139,250,0.25)' }}>
                        {prod?.esPeso ? 'kg' : 'ud'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-oranienbaum text-white/70 text-sm">
                        {prod?.esPeso ? `${fmt(prod?.stockSistema, 3)} kg` : `${fmt(prod?.stockSistema, 0)} ud`}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        {!prod?.esPeso && (
                          <div className="flex gap-0.5">
                            {[-1, 1, 5, 10]?.map(delta => (
                              <button
                                key={delta}
                                onClick={() => {
                                  const cur = contadoStr !== '' ? parseFloat(contadoStr) : prod?.stockSistema;
                                  const next = Math.max(0, (isNaN(cur) ? 0 : cur) + delta);
                                  setStockContado(prod?.id, String(next));
                                }}
                                className="w-7 h-7 rounded text-[10px] font-oranienbaum font-bold transition-colors"
                                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: delta < 0 ? RED : ACCENT }}
                              >
                                {delta > 0 ? `+${delta}` : delta}
                              </button>
                            ))}
                          </div>
                        )}
                        <input
                          type="number"
                          value={contadoStr}
                          onChange={e => setStockContado(prod?.id, e?.target?.value)}
                          onFocus={e => e?.target?.select()}
                          placeholder={prod?.esPeso ? '0.000' : '0'}
                          step={prod?.esPeso ? '0.001' : '1'}
                          min="0"
                          className="w-20 px-2 py-1.5 rounded-lg text-sm font-oranienbaum text-white/85 outline-none text-right"
                          style={{ background: 'rgba(255,255,255,0.06)', border: contadoStr !== '' ? `1px solid ${ACCENT}40` : '1px solid rgba(255,255,255,0.10)' }}
                        />
                        <span className="font-oranienbaum text-white/30 text-xs">{prod?.esPeso ? 'kg' : 'ud'}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-oranienbaum text-sm font-bold" style={{ color: difColor }}>
                        {diferencia === null ? '—' : diferencia === 0 ? '0' : diferencia > 0 ? `+${prod?.esPeso ? fmt(diferencia, 3) : fmt(diferencia, 0)}` : `${prod?.esPeso ? fmt(diferencia, 3) : fmt(diferencia, 0)}`}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-oranienbaum text-xs" style={{ color: difColor }}>
                        {diferencia === null ? '—' : diferencia === 0 ? '✓ Correcto' : diferencia > 0 ? '▲ Sobrante' : '▼ Faltante'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Historial de ajustes */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <h3 className="font-oranienbaum font-semibold text-white/70 text-sm">Historial de ajustes de inventario</h3>
          <button onClick={loadHistorial} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors"><Icon name="RefreshCw" size={12} /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                {['Fecha', 'Producto', 'PLU', 'Cantidad', 'Tipo ajuste', 'Usuario', 'Observaciones']?.map(h => (
                  <th key={h} className="px-3 py-2.5 text-left font-oranienbaum text-white/30 text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loadingHistorial ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center"><span className="font-oranienbaum text-white/30 text-sm">Cargando...</span></td></tr>
              ) : historial?.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center"><p className="font-oranienbaum text-white/25 text-sm">Sin historial de ajustes</p></td></tr>
              ) : historial?.map(mov => {
                const fecha = mov?.created_at ? new Date(mov?.created_at)?.toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                const cantidad = mov?.cantidad ?? 0;
                return (
                  <tr key={mov?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="hover:bg-white/[0.02]">
                    <td className="px-3 py-2.5 font-oranienbaum text-white/50 text-xs whitespace-nowrap">{fecha}</td>
                    <td className="px-3 py-2.5 font-oranienbaum text-white/70 text-xs max-w-[160px] truncate">{mov?.producto?.nombre || '—'}</td>
                    <td className="px-3 py-2.5 font-oranienbaum text-xs" style={{ color: BLUE }}>{mov?.producto?.plu || '—'}</td>
                    <td className="px-3 py-2.5 font-oranienbaum text-sm font-bold" style={{ color: cantidad > 0 ? ACCENT : RED }}>
                      {cantidad > 0 ? '+' : ''}{fmt(cantidad, 3)}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={cantidad > 0 ? { background: `${ACCENT}15`, color: ACCENT, border: `1px solid ${ACCENT}30` } : { background: `${RED}15`, color: RED, border: `1px solid ${RED}30` }}>
                        {cantidad > 0 ? 'Entrada' : 'Salida'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-oranienbaum text-white/40 text-xs">{mov?.usuario || '—'}</td>
                    <td className="px-3 py-2.5 font-oranienbaum text-white/40 text-xs max-w-[200px] truncate">{mov?.observaciones || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal confirmar */}
      {showConfirmar && (
        <ModalConfirmarRecuento
          items={itemsParaModal}
          onConfirm={handleAplicar}
          onClose={() => { setShowConfirmar(false); setSaveError(null); }}
          saving={saving}
        />
      )}
    </div>
  );
};

// ─── PÁGINA PRINCIPAL ─────────────────────────────────────────────────────────
const TiendaInventario = () => {
  const [vistaActiva, setVistaActiva] = useState('inventario'); // 'inventario' | 'recuento'
  const [activeTab, setActiveTab] = useState('Existencias');
  const [inventario, setInventario] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [movimientoItem, setMovimientoItem] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inv, fams, alms] = await Promise.all([
        tiendaDb?.from('inventario_actual')?.select(`
          id, producto_id, almacen_id, stock_actual, stock_reservado, stock_disponible,
          coste_medio, ultimo_coste, ultima_actualizacion,
          producto:productos(
            id, nombre, codigo_interno, ean, plu, activo, stock_minimo, stock_maximo, punto_pedido,
            tipo_iva, recargo_equivalencia, precio_compra, pvp_venta, pvp_sin_iva,
            margen_objetivo, margen_real, bloqueo_precio, familia_id,
            familia:familias_producto(id, nombre, margen_objetivo)
          ),
          almacen:almacenes(id, nombre)
        `)?.order('ultima_actualizacion', { ascending: false }),
        tiendaDb?.from('familias_producto')?.select('id, nombre, margen_objetivo, codigo_epelsa')?.order('nombre'),
        almacenesService?.getAll(),
      ]);
      setInventario(inv?.data || []);
      setFamilias(fams?.data || []);
      setAlmacenes(alms);
    } catch (e) { setError(e?.message); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleMovimiento = async (movData) => {
    await movimientosStockService?.create(movData);
    await loadAll();
  };

  // KPI summary
  const totalCoste = inventario?.reduce((s, i) => s + (i?.stock_actual || 0) * (i?.ultimo_coste || i?.producto?.precio_compra || 0), 0);
  const totalPvp = inventario?.reduce((s, i) => s + (i?.stock_actual || 0) * (i?.producto?.pvp_venta || 0), 0);
  const sinStock = inventario?.filter(i => (i?.stock_actual || 0) <= 0 && i?.producto?.activo)?.length;
  const bajoMinimo = inventario?.filter(i => (i?.stock_actual || 0) > 0 && (i?.stock_actual || 0) <= (i?.producto?.stock_minimo || 0))?.length;

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
              <Icon name="Package" size={18} style={{ color: ACCENT }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Inventario</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">Tienda física · Existencias · Valoración · Recuento</p>
        </div>
        <button onClick={loadAll} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
          <Icon name="RefreshCw" size={13} />Actualizar
        </button>
      </div>

      {/* Selector de vista principal */}
      <div className="flex items-center gap-1 p-1 rounded-xl mb-6 w-fit" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          onClick={() => setVistaActiva('inventario')}
          className="px-5 py-2 rounded-lg text-sm font-oranienbaum transition-all"
          style={vistaActiva === 'inventario' ? { background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}40` } : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}
        >
          Inventario Actual
        </button>
        <button
          onClick={() => setVistaActiva('recuento')}
          className="px-5 py-2 rounded-lg text-sm font-oranienbaum transition-all"
          style={vistaActiva === 'recuento' ? { background: `${GOLD}20`, color: GOLD, border: `1px solid ${GOLD}40` } : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}
        >
          Recuento Físico
        </button>
      </div>

      {/* ── VISTA INVENTARIO ACTUAL ── */}
      {vistaActiva === 'inventario' && (
        <>
          {/* KPI mini */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Valor a coste', value: fmtEur(totalCoste), color: BLUE, icon: 'Package' },
              { label: 'Valor potencial PVP', value: fmtEur(totalPvp), color: ACCENT, icon: 'TrendingUp' },
              { label: 'Sin stock', value: String(sinStock), color: RED, icon: 'XCircle' },
              { label: 'Bajo mínimo', value: String(bajoMinimo), color: YELLOW, icon: 'AlertTriangle' },
            ]?.map((k, i) => (
              <div key={i} className="rounded-2xl p-4 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${k?.color}15`, border: `1px solid ${k?.color}30` }}>
                  <Icon name={k?.icon} size={15} style={{ color: k?.color }} />
                </div>
                <div>
                  <p className="font-oranienbaum font-bold text-white text-lg">{k?.value}</p>
                  <p className="font-oranienbaum text-white/40 text-xs">{k?.label}</p>
                </div>
              </div>
            ))}
          </div>
          {error && <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}><Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" /><p className="font-oranienbaum text-red-400 text-sm">{error}</p></div>}
          {/* Tabs */}
          <div className="flex items-center gap-1 p-1 rounded-xl mb-6 w-fit" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {TABS?.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} className="px-4 py-2 rounded-lg text-sm font-oranienbaum transition-all" style={activeTab === tab ? { background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}40` } : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
                {tab}
              </button>
            ))}
          </div>
          {activeTab === 'Existencias' && <TabExistencias inventario={inventario} familias={familias} almacenes={almacenes} loading={loading} onMovimiento={setMovimientoItem} onRefresh={loadAll} />}
          {activeTab === 'Valoración' && <TabValoracion inventario={inventario} familias={familias} />}
          {activeTab === 'Rankings' && <TabRankings inventario={inventario} familias={familias} />}
          {activeTab === 'Rotación' && <TabRotacion inventario={inventario} />}
          {activeTab === 'Alertas' && <TabAlertas inventario={inventario} />}
        </>
      )}

      {/* ── VISTA RECUENTO FÍSICO ── */}
      {vistaActiva === 'recuento' && (
        <VistaRecuentoFisico familias={familias} onRefreshInventario={loadAll} />
      )}

      {movimientoItem && (
        <MovimientoModal item={movimientoItem} almacenes={almacenes} onSave={handleMovimiento} onClose={() => setMovimientoItem(null)} />
      )}
    </div>
  );
};

export default TiendaInventario;
